/** 属性倍率结算规则；默认使用所有单属性关系的几何平均。 */
export type TypeScaleMode = "average" | "legacy";

/** 限制属性对象只能由本模块中的预置单例初始化。 */
const typeToken = Symbol("Type");

/**
 * 属性基类。
 *
 * 单属性与双属性共享标识、名称和图标地址，并分别实现名称拆分、类型判定、
 * 克制倍率和本系判定逻辑。
 */
export abstract class Type {
    /** 属性图标使用的资源标识。 */
    readonly id: string;
    /** 属性的展示名称。 */
    readonly name: string;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param token token参数，参与本次操作
     * @param { id, name } { id, name }参数，参与本次操作
     */
    protected constructor(
        token: typeof typeToken,
        { id, name }: { /**
         * id，保存当前实例的id状态。
         */
        id: string; /**
         * name，保存当前实例的name状态。
         */
        name: string }
    ) {
        if (token !== typeToken) {
            throw new Error("Type 只能通过预置单例创建");
        }

        this.id = id;
        this.name = name;
    }

    /** 获取属性图标的云端资源地址。 */
    getImageSrc(): string {
        return `https://seerh5.61.com/resource/assets/PetType/${this.id}.png`;
    }

    /** 将属性名称拆分为单属性名称数组。 */
    abstract getTypeNameArray(): string[];

    /** 当前实例是否为单属性。 */
    abstract isSingleType(): boolean;

    /** 当前实例是否为双属性。 */
    abstract isDoubleType(): boolean;

    /**
     * 计算当前攻击属性作用于目标属性时的克制倍率。
     * @param type 作为防守方的目标属性
     */
    abstract getScale(type: Type, mode?: TypeScaleMode): number;

    /**
     * 判断目标精灵属性是否可视为当前技能属性的本系属性。
     * @param type 待判断的属性
     */
    abstract isSelf(type: Type): boolean;
}

/**
 * 单属性。
 *
 * 每个预置单例直接保存其克制、微弱和无效的目标单属性，未收录的关系按 1 倍处理。
 */
export class SingleType extends Type {
    // 预置单属性单例。关系必须在全部单例创建完成后再统一建立。
    /**
     * 草，保存当前实例的草状态。
     */
    static readonly 草 = new SingleType(typeToken, { id: "1", name: "草" });
    /**
     * 水，保存当前实例的水状态。
     */
    static readonly 水 = new SingleType(typeToken, { id: "2", name: "水" });
    /**
     * 火，保存当前实例的火状态。
     */
    static readonly 火 = new SingleType(typeToken, { id: "3", name: "火" });
    /**
     * 飞行，保存当前实例的飞行状态。
     */
    static readonly 飞行 = new SingleType(typeToken, { id: "4", name: "飞行" });
    /**
     * 电，保存当前实例的电状态。
     */
    static readonly 电 = new SingleType(typeToken, { id: "5", name: "电" });
    /**
     * 机械，保存当前实例的机械状态。
     */
    static readonly 机械 = new SingleType(typeToken, { id: "6", name: "机械" });
    /**
     * 地面，保存当前实例的地面状态。
     */
    static readonly 地面 = new SingleType(typeToken, { id: "7", name: "地面" });
    /**
     * 普通，保存当前实例的普通状态。
     */
    static readonly 普通 = new SingleType(typeToken, { id: "8", name: "普通" });
    /**
     * 冰，保存当前实例的冰状态。
     */
    static readonly 冰 = new SingleType(typeToken, { id: "9", name: "冰" });
    /**
     * 超能，保存当前实例的超能状态。
     */
    static readonly 超能 = new SingleType(typeToken, { id: "10", name: "超能" });
    /**
     * 战斗，保存当前实例的战斗状态。
     */
    static readonly 战斗 = new SingleType(typeToken, { id: "11", name: "战斗" });
    /**
     * 光，保存当前实例的光状态。
     */
    static readonly 光 = new SingleType(typeToken, { id: "12", name: "光" });
    /**
     * 暗影，保存当前实例的暗影状态。
     */
    static readonly 暗影 = new SingleType(typeToken, { id: "13", name: "暗影" });
    /**
     * 神秘，保存当前实例的神秘状态。
     */
    static readonly 神秘 = new SingleType(typeToken, { id: "14", name: "神秘" });
    /**
     * 龙，保存当前实例的龙状态。
     */
    static readonly 龙 = new SingleType(typeToken, { id: "15", name: "龙" });
    /**
     * 圣灵，保存当前实例的圣灵状态。
     */
    static readonly 圣灵 = new SingleType(typeToken, { id: "16", name: "圣灵" });
    /**
     * 次元，保存当前实例的次元状态。
     */
    static readonly 次元 = new SingleType(typeToken, { id: "17", name: "次元" });
    /**
     * 远古，保存当前实例的远古状态。
     */
    static readonly 远古 = new SingleType(typeToken, { id: "18", name: "远古" });
    /**
     * 邪灵，保存当前实例的邪灵状态。
     */
    static readonly 邪灵 = new SingleType(typeToken, { id: "19", name: "邪灵" });
    /**
     * 自然，保存当前实例的自然状态。
     */
    static readonly 自然 = new SingleType(typeToken, { id: "20", name: "自然" });
    /**
     * 王，保存当前实例的王状态。
     */
    static readonly 王 = new SingleType(typeToken, { id: "221", name: "王" });
    /**
     * 混沌，保存当前实例的混沌状态。
     */
    static readonly 混沌 = new SingleType(typeToken, { id: "222", name: "混沌" });
    /**
     * 神灵，保存当前实例的神灵状态。
     */
    static readonly 神灵 = new SingleType(typeToken, { id: "223", name: "神灵" });
    /**
     * 轮回，保存当前实例的轮回状态。
     */
    static readonly 轮回 = new SingleType(typeToken, { id: "224", name: "轮回" });
    /**
     * 虫，保存当前实例的虫状态。
     */
    static readonly 虫 = new SingleType(typeToken, { id: "225", name: "虫" });
    /**
     * 虚空，保存当前实例的虚空状态。
     */
    static readonly 虚空 = new SingleType(typeToken, { id: "226", name: "虚空" });
    /**
     * 属性，保存当前实例的属性状态。
     */
    static readonly 属性 = new SingleType(typeToken, { id: "prop", name: "属性" });

    /** 受到当前属性 2 倍克制的目标属性。 */
    readonly 克制: readonly SingleType[] = [];
    /** 受到当前属性 0.5 倍伤害的目标属性。 */
    readonly 微弱: readonly SingleType[] = [];
    /** 受到当前属性 0 倍伤害的目标属性。 */
    readonly 无效: readonly SingleType[] = [];

    static {
        // 两阶段初始化避免在创建较早单例时引用尚未创建的目标单例。
        this.草.setRelations(
            [this.水, this.地面, this.光],
            [
                this.草,
                this.火,
                this.飞行,
                this.机械,
                this.圣灵,
                this.远古,
                this.混沌,
                this.神灵
            ],
            []
        );
        this.水.setRelations(
            [this.火, this.地面],
            [this.草, this.水, this.圣灵, this.自然, this.混沌, this.神灵],
            []
        );
        this.火.setRelations(
            [this.草, this.机械, this.冰],
            [this.水, this.火, this.圣灵, this.自然, this.混沌, this.神灵],
            []
        );
        this.飞行.setRelations(
            [this.草, this.战斗, this.虫],
            [this.电, this.机械, this.次元, this.邪灵, this.自然, this.混沌],
            []
        );
        this.电.setRelations(
            [this.水, this.飞行, this.暗影, this.次元, this.混沌, this.虚空],
            [this.草, this.电, this.神秘, this.圣灵, this.自然, this.神灵],
            [this.地面]
        );
        this.机械.setRelations(
            [this.冰, this.战斗, this.远古, this.邪灵, this.神灵],
            [this.水, this.火, this.电, this.机械, this.次元],
            []
        );
        this.地面.setRelations(
            [this.火, this.电, this.机械, this.王, this.轮回],
            [
                this.草,
                this.超能,
                this.暗影,
                this.龙,
                this.圣灵,
                this.自然,
                this.神灵,
                this.虫
            ],
            [this.飞行]
        );
        this.普通.setRelations([], [], []);
        this.冰.setRelations(
            [
                this.草,
                this.飞行,
                this.地面,
                this.次元,
                this.远古,
                this.轮回,
                this.虫
            ],
            [
                this.水,
                this.火,
                this.机械,
                this.冰,
                this.圣灵,
                this.混沌,
                this.神灵
            ],
            []
        );
        this.超能.setRelations(
            [this.战斗, this.神秘, this.自然],
            [this.机械, this.超能, this.虫],
            [this.光]
        );
        this.战斗.setRelations(
            [this.机械, this.冰, this.龙, this.圣灵],
            [this.超能, this.战斗, this.暗影, this.邪灵, this.王],
            []
        );
        this.光.setRelations(
            [this.超能, this.暗影, this.虫],
            [
                this.机械,
                this.冰,
                this.光,
                this.圣灵,
                this.邪灵,
                this.自然,
                this.神灵,
                this.轮回,
                this.虚空
            ],
            [this.草]
        );
        this.暗影.setRelations(
            [this.超能, this.暗影, this.次元],
            [this.机械, this.冰, this.光, this.圣灵, this.邪灵, this.神灵],
            []
        );
        this.神秘.setRelations(
            [
                this.电,
                this.神秘,
                this.圣灵,
                this.自然,
                this.王,
                this.神灵,
                this.轮回
            ],
            [this.地面, this.战斗, this.邪灵, this.混沌, this.虫],
            []
        );
        this.龙.setRelations(
            [this.冰, this.龙, this.圣灵, this.邪灵],
            [this.草, this.水, this.火, this.电, this.远古, this.虫],
            []
        );
        this.圣灵.setRelations(
            [this.草, this.水, this.火, this.电, this.冰, this.远古, this.虚空],
            [this.战斗, this.神秘, this.龙, this.轮回],
            []
        );
        this.次元.setRelations(
            [
                this.飞行,
                this.机械,
                this.超能,
                this.邪灵,
                this.自然,
                this.虫,
                this.虚空
            ],
            [this.冰, this.王, this.混沌, this.神灵, this.轮回],
            [this.暗影]
        );
        this.远古.setRelations(
            [this.草, this.飞行, this.神秘, this.龙, this.虚空],
            [this.机械, this.冰, this.王, this.轮回],
            []
        );
        this.邪灵.setRelations(
            [this.光, this.暗影, this.神秘, this.次元, this.自然],
            [
                this.机械,
                this.冰,
                this.超能,
                this.圣灵,
                this.王,
                this.混沌,
                this.轮回
            ],
            [this.神灵]
        );
        this.自然.setRelations(
            [
                this.草,
                this.水,
                this.火,
                this.飞行,
                this.电,
                this.地面,
                this.光,
                this.王,
                this.轮回
            ],
            [
                this.机械,
                this.超能,
                this.战斗,
                this.暗影,
                this.神秘,
                this.次元,
                this.邪灵,
                this.混沌,
                this.虚空
            ],
            []
        );
        this.王.setRelations(
            [this.战斗, this.暗影, this.次元, this.邪灵],
            [this.超能, this.自然, this.虫],
            []
        );
        this.混沌.setRelations(
            [
                this.飞行,
                this.冰,
                this.神秘,
                this.次元,
                this.邪灵,
                this.自然,
                this.神灵
            ],
            [this.电, this.机械, this.战斗, this.轮回],
            [this.虚空]
        );
        this.神灵.setRelations(
            [
                this.草,
                this.水,
                this.火,
                this.电,
                this.冰,
                this.远古,
                this.邪灵,
                this.混沌
            ],
            [this.机械, this.战斗, this.龙],
            []
        );
        this.轮回.setRelations(
            [this.光, this.暗影, this.圣灵, this.次元, this.邪灵, this.混沌],
            [this.冰, this.超能, this.自然, this.虚空],
            []
        );
        this.虫.setRelations(
            [this.草, this.地面, this.战斗, this.混沌, this.虫],
            [this.水, this.火, this.冰, this.光],
            []
        );
        this.虚空.setRelations(
            [this.超能, this.战斗, this.光, this.神秘, this.自然, this.轮回],
            [this.飞行, this.暗影, this.圣灵, this.次元],
            []
        );
        this.属性.setRelations([], [], []);
    }

    /**
     * 创建实例并初始化所需的领域状态。
     * @param token token参数，参与本次操作
     * @param { id, name } { id, name }参数，参与本次操作
     */
    private constructor(
        token: typeof typeToken,
        { id, name }: { /**
         * id，保存当前实例的id状态。
         */
        id: string; /**
         * name，保存当前实例的name状态。
         */
        name: string }
    ) {
        super(token, { id, name });
    }

    /**
     * 填充并冻结单属性关系数组，使全局预置规则在初始化后不可被修改。
     */
    private setRelations(
        克制: readonly SingleType[],
        微弱: readonly SingleType[],
        无效: readonly SingleType[]
    ): void {
        (this.克制 as SingleType[]).push(...克制);
        (this.微弱 as SingleType[]).push(...微弱);
        (this.无效 as SingleType[]).push(...无效);
        Object.freeze(this.克制);
        Object.freeze(this.微弱);
        Object.freeze(this.无效);
        // Type 单例会嵌套在 Vue 的 reactive 战斗状态中。冻结完成后的
        // 单例不会被 Vue 转成 Proxy，从而保留关系数组依赖的对象身份。
        Object.freeze(this);
    }

    /**
     * getTypeNameArray：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override getTypeNameArray(): string[] {
        return [this.name];
    }

    /**
     * isSingleType：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override isSingleType(): boolean {
        return true;
    }

    /**
     * isDoubleType：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override isDoubleType(): boolean {
        return false;
    }

    /**
     * getScale：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @param mode mode参数，参与本次操作；默认值为 `"average"`
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override getScale(type: Type, mode: TypeScaleMode = "average"): number {
        if (mode === "average") return getGeometricMeanScale(this, type);
        if (type instanceof DoubleType) {
            // 单属性攻击双属性时，分别计算两个防守属性后套用双属性合成规则。
            return combineDoubleTypeScale([
                getSingleTypeScale(this, type.firstType),
                getSingleTypeScale(this, type.secondType)
            ]);
        }

        return getSingleTypeScale(this, type);
    }

    /**
     * isSelf：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override isSelf(type: Type): boolean {
        if (this === type) {
            return true;
        }

        // 单属性技能命中包含该属性的双属性精灵时，同样享受本系加成。
        return type instanceof DoubleType && type.hasType(this);
    }
}

/**
 * 双属性。
 *
 * 仅保存两个组成它的单属性；克制关系由两个单属性的倍率实时合成。
 */
export class DoubleType extends Type {
    /**
     * 水·超能，保存当前实例的水·超能状态。
     */
    static readonly 水·超能 = new DoubleType({
        id: "24",
        firstType: SingleType.水,
        secondType: SingleType.超能
    });
    /**
     * 水·暗影，保存当前实例的水·暗影状态。
     */
    static readonly 水·暗影 = new DoubleType({
        id: "25",
        firstType: SingleType.水,
        secondType: SingleType.暗影
    });
    /**
     * 飞行·神秘，保存当前实例的飞行·神秘状态。
     */
    static readonly 飞行·神秘 = new DoubleType({
        id: "53",
        firstType: SingleType.飞行,
        secondType: SingleType.神秘
    });
    /**
     * 圣灵·自然，保存当前实例的圣灵·自然状态。
     */
    static readonly 圣灵·自然 = new DoubleType({
        id: "88",
        firstType: SingleType.圣灵,
        secondType: SingleType.自然
    });
    /**
     * 飞行·火，保存当前实例的飞行·火状态。
     */
    static readonly 飞行·火 = new DoubleType({
        id: "27",
        firstType: SingleType.飞行,
        secondType: SingleType.火
    });
    /**
     * 地面·暗影，保存当前实例的地面·暗影状态。
     */
    static readonly 地面·暗影 = new DoubleType({
        id: "42",
        firstType: SingleType.地面,
        secondType: SingleType.暗影
    });

    /** 第一个组成属性。 */
    readonly firstType: SingleType;
    /** 第二个组成属性。 */
    readonly secondType: SingleType;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param {
        id,
        firstType,
        secondType
    } {
        id,
        firstType,
        secondType
    }参数，参与本次操作
     */
    private constructor({
        id,
        firstType,
        secondType
    }: {
        /**
         * id，保存当前实例的id状态。
         */
        id: string;
        /**
         * firstType，保存当前实例的firstType状态。
         */
        firstType: SingleType;
        /**
         * secondType，保存当前实例的secondType状态。
         */
        secondType: SingleType;
    }) {
        super(typeToken, {
            id,
            name: `${firstType.name}·${secondType.name}`
        });
        this.firstType = firstType;
        this.secondType = secondType;
        // 双属性同样是不可变的预置单例，避免被响应式状态深度代理。
        Object.freeze(this);
    }

    /**
     * getTypeNameArray：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override getTypeNameArray(): string[] {
        return [this.firstType.name, this.secondType.name];
    }

    /**
     * isSingleType：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override isSingleType(): boolean {
        return false;
    }

    /**
     * isDoubleType：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override isDoubleType(): boolean {
        return true;
    }

    /**
     * getScale：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @param mode mode参数，参与本次操作；默认值为 `"average"`
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override getScale(type: Type, mode: TypeScaleMode = "average"): number {
        if (mode === "average") return getGeometricMeanScale(this, type);
        if (type instanceof DoubleType) {
            // 双攻双防先计算对每个防守属性的合成倍率，再取两者平均值。
            return average([
                this.getScaleAgainstSingleType(type.firstType),
                this.getScaleAgainstSingleType(type.secondType)
            ]);
        }

        // 双属性攻击单属性时，将两个攻击属性的基础倍率合成为最终倍率。
        return this.getScaleAgainstSingleType(type);
    }

    /**
     * isSelf：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override isSelf(type: Type): boolean {
        return this === type;
    }

    /**
     * hasType：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    hasType(type: Type): boolean {
        return this.firstType === type || this.secondType === type;
    }

    /** 计算双属性攻击一个单属性目标时的合成倍率。 */
    private getScaleAgainstSingleType(type: Type): number {
        return combineDoubleTypeScale([
            getSingleTypeScale(this.firstType, type),
            getSingleTypeScale(this.secondType, type)
        ]);
    }
}

/** 新规则：取所有单属性攻防关系的几何平均，原无效关系改为绝对微弱。 */
function getGeometricMeanScale(attacker: Type, defender: Type): number {
    /**
     * attacks，供本模块相关规则使用。
     */
    const attacks = attacker instanceof DoubleType
        ? [attacker.firstType, attacker.secondType] : [attacker];
    /**
     * defenses，供本模块相关规则使用。
     */
    const defenses = defender instanceof DoubleType
        ? [defender.firstType, defender.secondType] : [defender];
    /**
     * result，供本模块相关规则使用。
     */
    let result = 1;
    for (const attack of attacks) {
        for (const defense of defenses) {
            /**
             * scale，供本模块相关规则使用。
             */
            const scale = attack instanceof SingleType ? getSingleTypeScale(attack, defense) : 1;
            result *= scale === 0 ? 0.25 : scale;
        }
    }
    return result ** (1 / (attacks.length * defenses.length));
}

/** 查询一个单属性攻击目标单属性时的基础倍率。 */
function getSingleTypeScale(attacker: SingleType, defender: Type): number {
    if (!(defender instanceof SingleType)) {
        return 1;
    }
    if (attacker.克制.includes(defender)) {
        return 2;
    }
    if (attacker.微弱.includes(defender)) {
        return 0.5;
    }
    if (attacker.无效.includes(defender)) {
        return 0;
    }
    return 1;
}

/**
 * 合成涉及双属性时的一对基础倍率。
 *
 * 任一基础倍率为 0 时将两者之和除以 4；两个基础倍率均为 2 时叠加为 4；
 * 其余组合取算术平均值。
 */
function combineDoubleTypeScale(scales: readonly [number, number]): number {
    if (scales.includes(0)) {
        return (scales[0] + scales[1]) / 4;
    }
    if (scales.every((scale) => scale === 2)) {
        return 4;
    }
    return average(scales);
}

/** 计算一对倍率的算术平均值。 */
function average(values: readonly [number, number]): number {
    return (values[0] + values[1]) / 2;
}

/**
 * 计算左侧攻击属性作用于右侧防守属性时的克制倍率。
 */
export function calcScale(left: Type, right: Type, mode: TypeScaleMode = "average"): number {
    return left.getScale(right, mode);
}

// 单属性中文导出别名，供精灵与技能定义直接引用。
/**
 * 草，供本模块相关规则使用。
 */
export const 草 = SingleType.草;
/**
 * 水，供本模块相关规则使用。
 */
export const 水 = SingleType.水;
/**
 * 火，供本模块相关规则使用。
 */
export const 火 = SingleType.火;
/**
 * 飞行，供本模块相关规则使用。
 */
export const 飞行 = SingleType.飞行;
/**
 * 电，供本模块相关规则使用。
 */
export const 电 = SingleType.电;
/**
 * 机械，供本模块相关规则使用。
 */
export const 机械 = SingleType.机械;
/**
 * 地面，供本模块相关规则使用。
 */
export const 地面 = SingleType.地面;
/**
 * 普通，供本模块相关规则使用。
 */
export const 普通 = SingleType.普通;
/**
 * 冰，供本模块相关规则使用。
 */
export const 冰 = SingleType.冰;
/**
 * 超能，供本模块相关规则使用。
 */
export const 超能 = SingleType.超能;
/**
 * 战斗，供本模块相关规则使用。
 */
export const 战斗 = SingleType.战斗;
/**
 * 光，供本模块相关规则使用。
 */
export const 光 = SingleType.光;
/**
 * 暗影，供本模块相关规则使用。
 */
export const 暗影 = SingleType.暗影;
/**
 * 神秘，供本模块相关规则使用。
 */
export const 神秘 = SingleType.神秘;
/**
 * 龙，供本模块相关规则使用。
 */
export const 龙 = SingleType.龙;
/**
 * 圣灵，供本模块相关规则使用。
 */
export const 圣灵 = SingleType.圣灵;
/**
 * 次元，供本模块相关规则使用。
 */
export const 次元 = SingleType.次元;
/**
 * 远古，供本模块相关规则使用。
 */
export const 远古 = SingleType.远古;
/**
 * 邪灵，供本模块相关规则使用。
 */
export const 邪灵 = SingleType.邪灵;
/**
 * 自然，供本模块相关规则使用。
 */
export const 自然 = SingleType.自然;
/**
 * 王，供本模块相关规则使用。
 */
export const 王 = SingleType.王;
/**
 * 混沌，供本模块相关规则使用。
 */
export const 混沌 = SingleType.混沌;
/**
 * 神灵，供本模块相关规则使用。
 */
export const 神灵 = SingleType.神灵;
/**
 * 轮回，供本模块相关规则使用。
 */
export const 轮回 = SingleType.轮回;
/**
 * 虫，供本模块相关规则使用。
 */
export const 虫 = SingleType.虫;
/**
 * 虚空，供本模块相关规则使用。
 */
export const 虚空 = SingleType.虚空;
/**
 * 属性，供本模块相关规则使用。
 */
export const 属性 = SingleType.属性;

// 双属性中文导出别名。
/**
 * 水·超能，供本模块相关规则使用。
 */
export const 水·超能 = DoubleType.水·超能;
/**
 * 水·暗影，供本模块相关规则使用。
 */
export const 水·暗影 = DoubleType.水·暗影;
/**
 * 飞行·神秘，供本模块相关规则使用。
 */
export const 飞行·神秘 = DoubleType.飞行·神秘;
/**
 * 圣灵·自然，供本模块相关规则使用。
 */
export const 圣灵·自然 = DoubleType.圣灵·自然;
/**
 * 飞行·火，供本模块相关规则使用。
 */
export const 飞行·火 = DoubleType.飞行·火;
/**
 * 地面·暗影，供本模块相关规则使用。
 */
export const 地面·暗影 = DoubleType.地面·暗影;

// 属性关系初始化后保持真正不可变；响应式适配器也不会代理这些单例，
// 因而界面运行和纯领域测试使用同一套身份、克制与本系判定。
for (const type of [...Object.values(SingleType), ...Object.values(DoubleType)]) {
    if (!(type instanceof Type)) continue;
    if (type instanceof SingleType) {
        Object.freeze(type.克制);
        Object.freeze(type.微弱);
        Object.freeze(type.无效);
    }
    Object.freeze(type);
}
